import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList, fmtDate } from "@/mocks/anime";

const PAGE_SIZE = 4;
const ranges = [
  { key: "all", label: "全部时间" },
  { key: "7", label: "近一周" },
  { key: "30", label: "近一个月" },
];

const categories = ["全部", "作品动态", "行业观察", "剧情解读", "档期情报", "音乐资讯", "活动情报", "网站指南", "作品回顾"];

export default function NewsPage() {
  const [range, setRange] = useState("all");
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);

  usePageMeta(
    "妖精动漫 - 动漫资讯 - 作品动态与行业观察",
    "妖精动漫资讯频道，提供最新动漫作品动态、档期情报、剧情解读与行业观察，帮助动画爱好者快速掌握圈内新鲜事。"
  );

  const today = new Date("2026-10-18");

  const filtered = useMemo(() => {
    const list = newsList.filter((item) => {
      if (category !== "全部" && item.category !== category) return false;
      if (range !== "all") {
        const diff = (today.getTime() - new Date(item.date).getTime()) / 86400000;
        if (diff > Number(range)) return false;
      }
      return true;
    });
    return [...list].sort((a, b) => (sort === "desc" ? (a.date < b.date ? 1 : -1) : a.date < b.date ? -1 : 1));
  }, [range, category, sort]);

  useEffect(() => {
    setPage(1);
  }, [range, category, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <SiteLayout>
      <section className="w-full bg-primary-950">
        <div className="w-full px-4 md:px-6 py-9 md:py-12">
          <p className="text-[11px] font-bold tracking-[0.2em] text-accent-400 mb-3">ANIME NEWS</p>
          <h1 className="text-2xl md:text-3xl font-black tracking-tight text-background-50">动漫资讯</h1>
          <p className="mt-2 text-[13px] md:text-[14px] leading-7 text-primary-200 max-w-[660px]">
            作品动态、档期情报、剧情解读与行业观察。这里记录每一部动画值得被记住的瞬间。
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {newsList.slice(0, 3).map((item) => (
              <Link
                key={item.id}
                to={`/news/detail/${item.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-900 text-primary-100 text-[12px] hover:bg-primary-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line text-accent-400" />
                {item.title.slice(0, 16)}…
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 lg:gap-8">
          <div className="min-w-0">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4 mb-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200 w-fit">
                  {ranges.map((r) => (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setRange(r.key)}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        range === r.key ? "bg-primary-900 text-background-50" : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setSort((v) => (v === "desc" ? "asc" : "desc"))}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-background-200 bg-background-50 text-[12px] font-medium text-foreground-700 whitespace-nowrap cursor-pointer hover:border-primary-300"
                >
                  <i className="ri-sort-desc" />
                  时间：{sort === "desc" ? "最新优先" : "最早优先"}
                </button>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      category === cat
                        ? "bg-accent-500 text-primary-950"
                        : "bg-background-50 text-foreground-600 border border-background-200 hover:text-primary-700"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>

            <p className="text-[12px] text-foreground-500 mb-3">
              共 <span className="font-bold text-foreground-950">{filtered.length}</span> 条资讯
            </p>

            {shown.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
                  <i className="ri-file-search-line text-2xl" />
                </span>
                <h2 className="mt-4 text-[15px] font-semibold text-foreground-950">这个筛选下暂时没有资讯</h2>
                <p className="mt-2 text-[13px] text-foreground-500">试试换一个时间范围或分类。</p>
                <button
                  type="button"
                  onClick={() => {
                    setRange("all");
                    setCategory("全部");
                  }}
                  className="mt-5 h-9 px-4 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[13px] font-bold transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部资讯
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {shown.map((news) => (
                  <Link
                    key={news.id}
                    to={`/news/detail/${news.id}`}
                    className="group flex flex-col sm:flex-row gap-4 p-3 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full sm:w-[220px] h-[170px] sm:h-[130px] rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={news.cover}
                        alt={`${news.title}资讯配图`}
                        title={`${news.title} - 妖精动漫资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
                        <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900 font-semibold">{news.category}</span>
                        <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {news.date}</span>
                        <span className="flex items-center gap-1"><i className="ri-user-3-line" /> {news.author}</span>
                      </div>
                      <h2 className="mt-2 text-[15px] md:text-[17px] font-bold text-foreground-950 leading-7 line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {news.title}
                      </h2>
                      <p className="mt-1.5 text-[12px] md:text-[13px] text-foreground-600 leading-6 line-clamp-2">{news.summary}</p>
                    </div>
                    <div className="hidden md:flex items-center text-foreground-400">
                      <i className="ri-arrow-right-s-line text-xl" />
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {filtered.length > 0 && (
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <p className="text-[12px] text-foreground-500">
                  第 {currentPage} / {totalPages} 页
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    className="h-8 px-3 rounded-md border border-background-200 bg-background-50 text-[12px] font-medium text-foreground-700 disabled:opacity-40 enabled:hover:border-primary-300 whitespace-nowrap cursor-pointer"
                  >
                    上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-8 h-8 rounded-md text-[12px] font-semibold transition-colors cursor-pointer ${
                        currentPage === i + 1 ? "bg-primary-900 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    className="h-8 px-3 rounded-md border border-background-200 bg-background-50 text-[12px] font-medium text-foreground-700 disabled:opacity-40 enabled:hover:border-primary-300 whitespace-nowrap cursor-pointer"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-[88px] lg:self-start space-y-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-4">
              <h2 className="text-[13px] font-bold text-foreground-950 mb-3">近期热门资讯</h2>
              <div className="space-y-3">
                {newsList.slice(0, 4).map((news, i) => (
                  <Link key={news.id} to={`/news/detail/${news.id}`} className="flex gap-2 group cursor-pointer">
                    <span className="text-[13px] font-black text-accent-700 w-3 shrink-0">{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-[12px] font-medium text-foreground-800 leading-5 line-clamp-2 group-hover:text-primary-700 transition-colors">
                        {news.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-400">{fmtDate(news.date)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            <div className="rounded-lg bg-primary-950 p-4">
              <h2 className="text-[13px] font-bold text-background-50 mb-2">想找一部番？</h2>
              <p className="text-[12px] leading-6 text-primary-200">用站内搜索同时查找动漫作品与资讯文章。</p>
              <Link
                to="/search"
                className="mt-3 inline-flex items-center justify-center gap-1.5 h-9 w-full rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-[12px] font-bold transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-line" /> 打开搜索
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}